/**
 * GpsService — GPS tracking with Capacitor native + browser fallback.
 *
 * On native (iOS/Android): uses Capacitor Geolocation plugin for background tracking.
 * On browser: uses navigator.geolocation (foreground only).
 *
 * NOTE: No static imports of @capacitor/* — all Capacitor access is via
 * window.Capacitor.Plugins so this file builds without Capacitor installed.
 */

import { isNative } from '../utils/platform';

function getCapacitorGeolocation() {
  // Capacitor injects plugins on window.Capacitor.Plugins in native builds
  return window.Capacitor?.Plugins?.Geolocation || null;
}

export class GpsService {
  constructor({ onPoint, onError, pushIntervalMs = 30000 }) {
    this.onPoint = onPoint;
    this.onError = onError;
    this.pushIntervalMs = pushIntervalMs;
    this.watchId = null;
    this.browserWatchId = null;
    this.ws = null;
    this.lastPushAt = 0;
  }

  // ─── Permissions ──────────────────────────────────────────────────

  async requestPermission() {
    if (!isNative) return true;
    const geo = getCapacitorGeolocation();
    if (!geo) return true;
    try {
      const status = await geo.requestPermissions({ permissions: ['location', 'coarseLocation'] });
      const granted = status.location === 'granted' || status.location === 'limited';
      if (!granted) {
        this.onError('Brak uprawnień do lokalizacji. Wejdź w Ustawienia > DuoFit > Lokalizacja.');
      }
      return granted;
    } catch {
      return true;
    }
  }

  async checkPermission() {
    if (!isNative) return 'granted';
    const geo = getCapacitorGeolocation();
    if (!geo) return 'granted';
    try {
      const status = await geo.checkPermissions();
      if (status.location === 'granted' || status.location === 'limited') return 'granted';
      if (status.location === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'granted';
    }
  }

  // ─── Tracking ─────────────────────────────────────────────────────

  async startTracking(ws) {
    if (this.watchId || this.browserWatchId !== null) {
      console.warn('[GPS] Already tracking.');
      return;
    }
    this.ws = ws;
    if (isNative) {
      await this._startNative();
    } else {
      this._startBrowser();
    }
  }

  stopTracking() {
    if (isNative && this.watchId) {
      const geo = getCapacitorGeolocation();
      if (geo) geo.clearWatch({ id: this.watchId });
      this.watchId = null;
    }
    if (!isNative && this.browserWatchId !== null) {
      navigator.geolocation.clearWatch(this.browserWatchId);
      this.browserWatchId = null;
    }
    this.ws = null;
    this.lastPushAt = 0;
  }

  // ─── Native (Capacitor) ───────────────────────────────────────────

  async _startNative() {
    const geo = getCapacitorGeolocation();
    if (!geo) {
      this._startBrowser(); // fallback
      return;
    }
    try {
      this.watchId = await geo.watchPosition(
        { enableHighAccuracy: true, timeout: 15000 },
        (position, err) => {
          if (err) { this.onError(`GPS error: ${err.message}`); return; }
          if (!position) return;
          const point = this._mapPosition(position);
          this.onPoint(point);
          this._maybePushToWs(point);
        }
      );
    } catch (e) {
      this.onError(`Nie można uruchomić GPS: ${e.message || e}`);
    }
  }

  // ─── Browser fallback ─────────────────────────────────────────────

  _startBrowser() {
    if (!navigator.geolocation) {
      this.onError('Twoja przeglądarka nie obsługuje geolokalizacji.');
      return;
    }
    this.browserWatchId = navigator.geolocation.watchPosition(
      (position) => {
        const point = this._mapPosition(position);
        this.onPoint(point);
        this._maybePushToWs(point);
      },
      (err) => this.onError(`GPS error (${err.code}): ${err.message}`),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
  }

  // ─── Single position read ─────────────────────────────────────────

  async getCurrentPosition() {
    try {
      const geo = isNative ? getCapacitorGeolocation() : null;
      if (geo) {
        const pos = await geo.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
        return this._mapPosition(pos);
      }
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(this._mapPosition(pos)),
          (err) => reject(err),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      });
    } catch {
      return null;
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────

  _mapPosition(position) {
    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude ?? null,
      altitudeAccuracy: position.coords.altitudeAccuracy ?? null,
      speed: position.coords.speed ?? null,
      heading: position.coords.heading ?? null,
      timestamp: position.timestamp,
    };
  }

  _maybePushToWs(point) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - this.lastPushAt < this.pushIntervalMs) return;
    this.lastPushAt = now;
    this.ws.send(JSON.stringify({
      type: 'gps_track',
      lat: point.lat,
      lng: point.lng,
      accuracy: point.accuracy,
      altitude: point.altitude,
      speed: point.speed,
      heading: point.heading,
      timestamp: point.timestamp,
    }));
  }

  // ─── Static utilities ─────────────────────────────────────────────

  static formatPace(speedMs) {
    if (!speedMs || speedMs <= 0) return '--:--';
    const minPerKm = 1000 / speedMs / 60;
    const mins = Math.floor(minPerKm);
    const secs = Math.round((minPerKm - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  static distanceMeters(a, b) {
    const R = 6371000;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const c = sinDLat * sinDLat +
      Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * sinDLng * sinDLng;
    return R * 2 * Math.atan2(Math.sqrt(c), Math.sqrt(1 - c));
  }
}
